import { apiFetch } from '../api/api';

// Cache for global units configuration
let globalUnitsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Common unit mappings for auto-detection
const UNIT_FIELD_MAPPINGS = {
  depth: ['depth', 'length', 'distance', 'elevation', 'height', 'td', 'measured', 'vertical'],
  temperature: ['temp', 'temperature', 'heat'],
  pressure: ['pressure', 'press', 'psi', 'bar', 'pascal'],
  weight: ['weight', 'mass', 'load', 'force'],
  volume: ['volume', 'flow', 'rate', 'capacity'],
  time: ['time', 'duration', 'hours', 'minutes', 'seconds'],
  speed: ['speed', 'rate', 'velocity', 'rop', 'rpm'],
  angle: ['angle', 'direction', 'inclination', 'azimuth', 'bearing']
};

// Fetch and cache global units configuration
export const getGlobalUnitsConfig = async () => {
  const now = Date.now();
  
  // Return cached data if it's still fresh
  if (globalUnitsCache && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
    return globalUnitsCache;
  }
  
  try {
    const config = await apiFetch('/api/global-units');
    globalUnitsCache = config;
    cacheTimestamp = now;
    return config;
  } catch (error) {
    console.error('Failed to fetch global units config:', error);
    return globalUnitsCache || null; // Return cached data if available
  }
};

// Get global unit settings only
export const getGlobalUnitSettings = async () => {
  try {
    const settings = await apiFetch('/api/global-units/settings');
    return settings;
  } catch (error) {
    console.error('Failed to fetch global unit settings:', error);
    return null;
  }
};

// Update global unit settings
export const updateGlobalUnitSettings = async (newSettings) => {
  try {
    const result = await apiFetch('/api/global-units/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newSettings)
    });
    
    // Clear cache to force refresh
    clearGlobalUnitsCache();
    
    return result;
  } catch (error) {
    console.error('Failed to update global unit settings:', error);
    throw error;
  }
};

// Get unit categories
export const getUnitCategories = async () => {
  try {
    const categories = await apiFetch('/api/global-units/categories');
    return categories;
  } catch (error) {
    console.error('Failed to fetch unit categories:', error);
    return null;
  }
};

// Clear cache (useful for testing or manual refresh)
export const clearGlobalUnitsCache = () => {
  globalUnitsCache = null;
  cacheTimestamp = null;
};

// Resolve a unit from global settings
export const resolveGlobalUnit = async (category) => {
  try {
    const resolved = await apiFetch(`/api/global-units/resolve/${category}`);
    return resolved;
  } catch (error) {
    console.error(`Failed to resolve global unit for category "${category}":`, error);
    return null;
  }
};

// Auto-detect unit category based on field name or label
export const detectUnitCategory = (fieldName, fieldLabel = '') => {
  const searchText = `${fieldName} ${fieldLabel}`.toLowerCase();
  
  for (const [category, keywords] of Object.entries(UNIT_FIELD_MAPPINGS)) {
    for (const keyword of keywords) {
      if (searchText.includes(keyword)) {
        return category;
      }
    }
  }
  
  return null; // No category detected
};

// Resolve unit for a field (handles "global" units)
export const resolveFieldUnit = async (field) => {
  // If unit is explicitly set and not "global", return it
  if (field.unit && field.unit.toLowerCase() !== 'global') {
    return field.unit;
  }
  
  // If unit is "global" or not set, try to resolve from global settings
  if (field.unit && field.unit.toLowerCase() === 'global') {
    // Try to detect category from field name/label
    const detectedCategory = detectUnitCategory(field.name, field.label);
    if (detectedCategory) {
      const settings = await getGlobalUnitSettings();
      return settings?.[detectedCategory] || field.unit;
    }
  }
  
  return field.unit || ''; // Return original unit or empty string
};

// Get all available units for a category
export const getAvailableUnitsForCategory = async (category) => {
  try {
    const config = await getGlobalUnitsConfig();
    return config?.unitCategories?.[category]?.availableUnits || [];
  } catch (error) {
    console.error(`Failed to get available units for category "${category}":`, error);
    return [];
  }
};

// Check if a unit is valid for a category
export const isValidUnitForCategory = async (category, unit) => {
  try {
    const availableUnits = await getAvailableUnitsForCategory(category);
    return availableUnits.some(u => u.value === unit);
  } catch (error) {
    console.error('Failed to validate unit:', error);
    return false;
  }
};

// Get unit display information (label, symbol, etc.)
export const getUnitDisplayInfo = async (category, unit) => {
  try {
    const availableUnits = await getAvailableUnitsForCategory(category);
    return availableUnits.find(u => u.value === unit) || null;
  } catch (error) {
    console.error('Failed to get unit display info:', error);
    return null;
  }
};

// Format field with resolved unit
export const formatFieldWithUnit = async (field, value) => {
  try {
    const resolvedUnit = await resolveFieldUnit(field);
    if (resolvedUnit && value !== null && value !== undefined && value !== '') {
      return `${value} ${resolvedUnit}`;
    }
    return value;
  } catch (error) {
    console.error('Failed to format field with unit:', error);
    return value;
  }
};

// Synchronous version for when config is already loaded
export const resolveFieldUnitSync = (field, globalSettings = null) => {
  // If unit is explicitly set and not "global", return it
  if (field.unit && field.unit.toLowerCase() !== 'global') {
    return field.unit;
  }
  
  // If unit is "global" and we have global settings, try to resolve
  if (field.unit && field.unit.toLowerCase() === 'global' && globalSettings) {
    const detectedCategory = detectUnitCategory(field.name, field.label);
    if (detectedCategory && globalSettings[detectedCategory]) {
      return globalSettings[detectedCategory];
    }
  }
  
  return field.unit || '';
};

export default {
  getGlobalUnitsConfig,
  getGlobalUnitSettings,
  updateGlobalUnitSettings,
  getUnitCategories,
  clearGlobalUnitsCache,
  resolveGlobalUnit,
  detectUnitCategory,
  resolveFieldUnit,
  getAvailableUnitsForCategory,
  isValidUnitForCategory,
  getUnitDisplayInfo,
  formatFieldWithUnit,
  resolveFieldUnitSync
}; 